import React from 'react';
import { FileX, FolderOpen, Search, Loader, type LucideIcon } from 'lucide-react';

interface EmptyStateProps {
  icon?: 'documents' | 'events' | 'search' | 'jobs' | 'generic' | LucideIcon;
  title?: string;
  message?: string;
  cta?: { label: string; onClick: () => void };
  secondaryCta?: { label: string; onClick: () => void };
  loading?: boolean;
  className?: string;
  compact?: boolean;
}

const iconMap = {
  documents: FileX,
  events: FolderOpen,
  search: Search,
  jobs: Search,
  generic: FolderOpen,
};

export default function EmptyState({
  icon = 'generic',
  title = 'Nothing here yet',
  message = 'This section will fill up once there is data to show.',
  cta,
  secondaryCta,
  loading,
  className = '',
  compact = false,
}: EmptyStateProps) {
  const Icon = typeof icon === 'string' ? iconMap[icon] || iconMap.generic : icon;

  return (
    <div
      className={`empty-state-surface flex flex-col items-center justify-center text-center px-6 rounded-[1.5rem] border border-dashed transition-all ${compact ? 'py-8' : 'py-14 sm:py-20'} ${className}`}
      aria-busy={loading}
    >
      <div className="empty-state-icon-surface w-14 h-14 rounded-2xl flex items-center justify-center mb-4 shadow-inner">
        {loading ? (
          <Loader className="h-6 w-6 text-brand animate-spin" />
        ) : (
          <Icon className="h-6 w-6 text-brand" strokeWidth={1.5} />
        )}
      </div>
      <h3 className="text-heading-s font-semibold text-slate-800 mb-1">{title}</h3>
      <p className="text-caption text-slate-500 max-w-[16rem] leading-relaxed">{message}</p>
      {(cta || secondaryCta) && !loading && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {cta && (
            <button
              onClick={cta.onClick}
              className="px-4 py-2 rounded-xl btn-primary text-white text-xs font-semibold shadow-sm hover:opacity-95 transition-all cursor-pointer"
            >
              {cta.label}
            </button>
          )}
          {secondaryCta && (
            <button
              onClick={secondaryCta.onClick}
              className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-xs font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 transition-all cursor-pointer"
            >
              {secondaryCta.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
